import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Compass, Shield, ArrowRight, Mail, Phone, MapPin, CheckCircle2, Briefcase, Settings2, Zap, ClipboardCheck, ShieldAlert, Users, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services as mockServices, references as mockReferences, whyUs as mockWhyUs } from '../data';
import { fetchCmsCollection, normalizeService, normalizeReference, normalizeWhyUs } from '../lib/cms';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  },
  viewport: { once: true, margin: "-100px" }
};

const Hero = () => {
  const tags = ["Pharma", "PtX", "E-House", "UPS", "DS/EN 60364"];

  return (
    <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-center bg-slate-900 pt-20 pb-12 overflow-hidden">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 1.5 }}
        className="absolute inset-0 z-0"
      >
        <img src="/hero-bg.png" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
      </motion.div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
        <div className="max-w-3xl">
          <motion.h1 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-emerald-400 font-bold text-xs md:text-sm tracking-[0.2em] uppercase mb-3"
          >
            Green Energy Consulting ApS
          </motion.h1>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl md:text-6xl font-bold text-white leading-tight mb-4"
          >
            Jeres partner i projektledelse og engineering
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-slate-300 text-base md:text-lg leading-relaxed mb-8 max-w-2xl"
          >
            Vi hjælper Pharma, PtX og industri med projektledelse, teknisk projektledelse, engineering, dokumentationsreview, HSEQ og arbejdsmiljøkoordinering.
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 mb-10"
          >
            <a href="#kontakt" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold px-6 py-3 md:px-8 md:py-4 rounded-none transition-all flex items-center justify-center gap-2 group">
              Lad os tale om jeres behov
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="flex flex-wrap gap-2 md:gap-3"
          >
            {tags.map(tag => (
              <span key={tag} className="text-slate-400 border border-slate-800 px-3 py-1 text-[10px] md:text-xs font-medium uppercase tracking-wider">
                {tag}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const SectionHeading = ({ children, green }) => (
  <h2 className={`text-3xl md:text-4xl font-bold mb-12 ${green ? 'text-emerald-600' : 'text-slate-900'}`}>
    {children}
  </h2>
);

const ContactForm = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) setSubmitted(true);
    } catch (error) {
      console.error("Submission failed", error);
    }
  };

  if (submitted) {
    return (
      <div className="bg-emerald-50 p-12 text-center border border-emerald-200">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-slate-900 mb-2">Tak for din besked!</h3>
        <p className="text-slate-600">Vi vender tilbage til dig snarest muligt.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="sr-only absolute">
        <label htmlFor="website_url">Honeypot</label>
        <input type="text" id="website_url" name="website_url" tabIndex="-1" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Navn</label>
          <input required name="name" type="text" className="w-full bg-white border border-slate-300 p-4 rounded-none focus:outline-none focus:border-emerald-500 transition-colors" />
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Email</label>
          <input required name="email" type="email" className="w-full bg-white border border-slate-300 p-4 rounded-none focus:outline-none focus:border-emerald-500 transition-colors" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Besked</label>
        <textarea required name="message" rows="4" className="w-full bg-white border border-slate-300 p-4 rounded-none focus:outline-none focus:border-emerald-500 transition-colors"></textarea>
      </div>
      <button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-10 py-4 rounded-none transition-all w-full md:w-auto uppercase tracking-widest text-sm">
        Send besked
      </button>
      <p className="text-xs text-slate-500 mt-4">
        Ved at indsende accepterer du, at vi må kontakte dig via den oplyste email. Ingen tracking eller cookies.
      </p>
    </form>
  );
};

const Home = () => {
  const [servicesList, setServicesList] = useState(mockServices);
  const [referencesList, setReferencesList] = useState(mockReferences);
  const [whyUsList, setWhyUsList] = useState(mockWhyUs);

  useEffect(() => {
    document.title = "Green Energy Consulting - Projektledelse og Engineering";

    async function loadCmsData() {
      const [rawServices, rawReferences, rawWhyUs] = await Promise.all([
        fetchCmsCollection('services', mockServices),
        fetchCmsCollection('references', mockReferences),
        fetchCmsCollection('why_us', mockWhyUs)
      ]);

      if (rawServices && rawServices.length > 0) {
        setServicesList(rawServices.map(normalizeService));
      }
      if (rawReferences && rawReferences.length > 0) {
        setReferencesList(rawReferences.map(normalizeReference));
      }
      if (rawWhyUs && rawWhyUs.length > 0) {
        setWhyUsList(rawWhyUs.map(normalizeWhyUs));
      }
    }
    loadCmsData();
  }, []);

  return (
    <>
      <Hero />

      {/* Services Section */}
      <section id="ydelser" className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div 
            {...fadeIn}
            className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6"
          >
            <div className="max-w-2xl">
              <span className="text-emerald-600 font-bold text-sm tracking-widest uppercase mb-4 block">Vores Ekspertise</span>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">Ydelser tilpasset fremtidens energi og industri</h2>
            </div>
            <p className="text-slate-500 max-w-sm mb-2 font-medium">Specialiseret rådgivning med fokus på sikkerhed, kvalitet og fremdrift i komplekse projekter.</p>
          </motion.div>
          
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {servicesList.map((service, idx) => {
              const icons = {
                Briefcase: Briefcase,
                Settings2: Settings2,
                Zap: Zap,
                ClipboardCheck: ClipboardCheck,
                ShieldAlert: ShieldAlert,
                Users: Users
              };
              const Icon = icons[service.icon] || Briefcase;
              
              const isFirst = idx === 0;
              
              return (
                <motion.div 
                  key={idx} 
                  variants={fadeIn}
                  className={`relative p-8 border transition-all duration-300 
                    ${isFirst 
                      ? 'bg-slate-900 border-emerald-500 shadow-xl shadow-emerald-900/10' 
                      : 'bg-white border-slate-200 shadow-sm'
                    }`}
                >
                  <div className={`w-14 h-14 border flex items-center justify-center mb-8 transition-all duration-300 
                    ${isFirst 
                      ? 'bg-emerald-500 border-emerald-500' 
                      : 'bg-emerald-50 border-emerald-100'
                    }`}
                  >
                    <Icon className={`w-6 h-6 transition-colors duration-300 
                      ${isFirst 
                        ? 'text-slate-900' 
                        : 'text-emerald-600'
                      }`} 
                    />
                  </div>
                  <h3 className={`text-xl font-bold mb-4 transition-colors duration-300 
                    ${isFirst ? 'text-white' : 'text-slate-900'}`}
                  >
                    {service.title}
                  </h3>
                  <p className={`leading-relaxed text-sm md:text-base transition-colors duration-300 
                    ${isFirst ? 'text-slate-300' : 'text-slate-600'}`}
                  >
                    {service.description}
                  </p>
                  <div className={`absolute bottom-0 left-0 h-1 bg-emerald-500 transition-all duration-500 
                    ${isFirst ? 'w-full' : 'w-0'}`} 
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* References Section */}
      <section id="referencer" className="py-12 md:py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div {...fadeIn}>
            <SectionHeading green>Udvalgte referencer</SectionHeading>
          </motion.div>
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {referencesList.slice(0, 3).map((ref, idx) => (
              <motion.div 
                key={idx} 
                variants={fadeIn}
                className="bg-white border border-slate-200 overflow-hidden group flex flex-col"
              >
                <Link to={`/referencer/${ref.slug}`} className="h-64 overflow-hidden relative block">
                  <img src={ref.image} alt={ref.company} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-slate-900/20 group-hover:bg-slate-900/0 transition-colors"></div>
                </Link>
                <div className="p-8 flex-grow">
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest block mb-2">{ref.company}</span>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-emerald-600 transition-colors">
                    <Link to={`/referencer/${ref.slug}`}>{ref.title}</Link>
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">{ref.description}</p>
                </div>
                <div className="px-8 pb-8">
                  <Link 
                    to={`/referencer/${ref.slug}`} 
                    className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 hover:text-emerald-600 transition-colors group/link"
                  >
                    Læs mere
                    <ChevronRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-8 md:py-12 bg-slate-50 relative overflow-hidden">
        {/* Decorative Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <svg width="100%" height="100%">
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <motion.div {...fadeIn}>
            <SectionHeading green>Hvorfor vælge os</SectionHeading>
          </motion.div>
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-12"
          >
            {whyUsList.map((item, idx) => {
              const Icon = item.icon === "ShieldCheck" ? ShieldCheck : item.icon === "Compass" ? Compass : Shield;
              return (
                <motion.div 
                  key={idx} 
                  variants={fadeIn}
                  className="flex flex-col gap-6 p-6 transition-colors group"
                >
                  <div className="w-14 h-14 bg-white shadow-sm flex items-center justify-center rounded-lg transition-colors duration-300">
                    <Icon className="text-emerald-600 w-7 h-7 transition-colors" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-4 transition-colors">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="relative py-16 md:py-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <motion.div 
          initial={{ scale: 1.1, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
        >
          <img 
            src="/images/contact-bg.png" 
            alt="" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm"></div>
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-emerald-500 font-bold text-sm tracking-widest uppercase mb-4 block">Kontakt os</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
                Lad os tage en uforpligtende snak om jeres næste projekt
              </h2>
              <div className="space-y-8">
                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 group-hover:bg-emerald-500 transition-colors duration-300">
                    <Mail className="w-5 h-5 text-emerald-500 group-hover:text-slate-900 transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Email</h4>
                    <a href="mailto:ml@greenecon.dk" className="text-xl font-bold text-white hover:text-emerald-400 transition-colors">ml@greenecon.dk</a>
                  </div>
                </div>
                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 group-hover:bg-emerald-500 transition-colors duration-300">
                    <Phone className="w-5 h-5 text-emerald-500 group-hover:text-slate-900 transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Mobil</h4>
                    <a href="tel:+4526465885" className="text-xl font-bold text-white hover:text-emerald-400 transition-colors">26465885</a>
                  </div>
                </div>
                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 group-hover:bg-emerald-500 transition-colors duration-300">
                    <MapPin className="w-5 h-5 text-emerald-500 group-hover:text-slate-900 transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Adresse</h4>
                    <p className="text-xl font-bold text-white transition-colors">Spangvej 3</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white p-8 md:p-12 shadow-2xl relative"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
